/**
 * Client create/edit overlay + Firestore persistence.
 *
 * Created and edited clients are kept in a localStorage overlay so the UI
 * updates instantly and survives reloads even in demo mode. When the user is
 * signed in with commercial-edit rights, the same record is written to the OS
 * `customers` collection so it becomes real live data. The overlay is applied
 * on top of whatever loadAccounts/loadDemo returns, via useAccounts.
 */
import type { Account } from './accounts';
import { db } from './firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const LS_KEY = 'mfx-crm-client-overrides';
interface Overlay { created: Account[]; edits: Record<string, Partial<Account>>; }

function load(): Overlay {
  try { const raw = localStorage.getItem(LS_KEY); if (raw) return JSON.parse(raw) as Overlay; } catch { /* ignore */ }
  return { created: [], edits: {} };
}
let overlay: Overlay = load();

const subs = new Set<() => void>();
function persist() { try { localStorage.setItem(LS_KEY, JSON.stringify(overlay)); } catch { /* ignore */ } }
function notify() { subs.forEach((f) => f()); }

export function subscribeClientStore(fn: () => void): () => void {
  subs.add(fn);
  return () => { subs.delete(fn); };
}

/** Merge edits into matching accounts and prepend any created clients. */
export function applyOverlay(accounts: Account[]): Account[] {
  const edits = overlay.edits;
  const merged = accounts.map((a) => (edits[a.id] ? { ...a, ...edits[a.id] } : a));
  const present = new Set(merged.map((a) => a.id));
  const created = overlay.created
    .filter((c) => !present.has(c.id))
    .map((c) => (edits[c.id] ? { ...c, ...edits[c.id] } : c));
  return [...created, ...merged];
}

export function newClientId(): string {
  return `cust_local_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
}

function toCustomerDoc(a: Account): Record<string, any> {
  const d: Record<string, any> = {
    name: a.name,
    industry: a.industry || '',
    source: a.source || 'crm',
    website: a.website || '',
    phone: a.phone || '',
    fax: a.fax || '',
    rating: a.rating || '',
    contactName: a.contactName || '',
    email: a.email || '',
    billingStreet: a.billing?.street || '', billingCity: a.billing?.city || '',
    billingState: a.billing?.state || '', billingCode: a.billing?.postal || '', billingCountry: a.billing?.country || '',
    shippingStreet: a.shipping?.street || '', shippingCity: a.shipping?.city || '',
    shippingState: a.shipping?.state || '', shippingCode: a.shipping?.postal || '', shippingCountry: a.shipping?.country || '',
    deleted: a.status === 'inactive',
    crmStatus: a.status,
    notes: a.notes || '',
    updatedAt: serverTimestamp(),
  };
  if (typeof a.healthScore === 'number') d.healthScore = a.healthScore;
  if (a.tags && a.tags.length) d.tags = a.tags;
  return d;
}

async function persistToFirestore(a: Account, isNew: boolean): Promise<{ ok: boolean; error?: string }> {
  try {
    const docId = a.id.replace(/^cust_/, '').replace(/^vend_/, '');
    const payload = isNew
      ? { ...toCustomerDoc(a), createdAt: serverTimestamp(), createdVia: 'mfx-crm' }
      : toCustomerDoc(a);
    await setDoc(doc(db, 'customers', docId), payload, { merge: true });
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'write failed' };
  }
}

export async function createClient(a: Account): Promise<{ ok: boolean; account: Account; error?: string }> {
  overlay.created = [a, ...overlay.created.filter((c) => c.id !== a.id)];
  persist(); notify();
  const r = await persistToFirestore(a, true);
  return { ok: r.ok, account: a, error: r.error };
}

export async function updateClient(id: string, patch: Partial<Account>, full: Account): Promise<{ ok: boolean; error?: string }> {
  overlay.edits[id] = { ...(overlay.edits[id] || {}), ...patch };
  overlay.created = overlay.created.map((c) => (c.id === id ? { ...c, ...patch } : c));
  persist(); notify();
  return persistToFirestore(full, false);
}

/** True when this client only exists in the local overlay (not yet on the server). */
export function isLocalClient(id: string): boolean {
  return id.startsWith('cust_local_');
}
